import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, FileText, Pencil, Plus } from "lucide-react";

import { archiveCustomer, generateVirtualAccount, getCustomerDetails, unArchiveCustomer } from "src/api/customer";
import { deleteSubAccount } from "src/api/subAccount";
import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import EditCustomerModal from "src/components/modals/EditCustomerModal";
import AddSubAccount from "src/components/modals/AddSubAccount";
import { Button } from "src/components/ui/button";
import { Skeleton } from "src/components/ui/skeleton";
import { DataTable, EmptyState, type Column } from "src/components/common";
import { getCustomizationValue, renderCurrency, showSuccessToast } from "src/lib/utils";
import type { SubAccount } from "src/types/customer";

interface CustomerDetailModalProps {
	uuid: string | null;
	onOpenChange: (open: boolean) => void;
}

const initialsOf = (first?: string, last?: string) => `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "—";

const Field = ({ label, value }: { label: string; value?: string | null }) => (
	<div className="tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-px-3 tw-py-2.5">
		<div className="tw-mb-1 tw-text-[11.5px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-text-soft">{label}</div>
		<div className="tw-break-words tw-text-[13.5px] tw-text-text">{value || "Not added yet"}</div>
	</div>
);

const MiniStat = ({ label, value }: { label: string; value: string | number }) => (
	<div className="tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-px-4 tw-py-3.5">
		<div className="tw-mb-1 tw-text-[12.5px] tw-text-text-soft">{label}</div>
		<div className="tw-text-[17px] tw-font-bold tw-text-text">{value}</div>
	</div>
);

/**
 * Customer profile as a modal (decision D2 — the prototype shows this over the list rather
 * than on its own route). `/customers/:uuid` still resolves: it redirects to the list with
 * `?customer=<uuid>`, which opens this.
 */
const CustomerDetailModal = ({ uuid, onOpenChange }: CustomerDetailModalProps) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";
	const subAccountLabel = getCustomizationValue(user, "sub_account_label", true) || "Sub Account";
	const subAccountLabelPlural = getCustomizationValue(user, "sub_account_label") || "Sub Accounts";
	const invoiceLabel = getCustomizationValue(user, "invoice_label", true) || "Invoice";
	const isSchool = user?.organization_type_name === "Schools";

	const { data, isLoading } = useQuery({
		queryKey: ["customers", uuid],
		queryFn: () => getCustomerDetails(uuid as string),
		enabled: Boolean(uuid),
	});

	const detail = data?.data;
	const customer = detail?.contact_details;

	const invalidate = () => queryClient.invalidateQueries(["customers"]);

	const { mutate: generateStatement } = useMutation(generateVirtualAccount, {
		onSuccess: ({ message }) => {
			if (message) showSuccessToast(message);
			invalidate();
		},
	});

	const { mutate: archiveMutate, isLoading: isArchiving } = useMutation(
		customer?.is_archived ? unArchiveCustomer : archiveCustomer,
		{
			onSuccess: ({ message }) => {
				if (message) showSuccessToast(message);
				invalidate();
				onOpenChange(false);
			},
		}
	);

	const { mutate: removeSubAccount } = useMutation(deleteSubAccount, {
		onSuccess: ({ message }) => {
			if (message) showSuccessToast(message);
			invalidate();
		},
	});

	const subAccountColumns: Column<SubAccount>[] = [
		{ key: "first", header: "First Name", cell: (row) => row.firstname ?? "—", primary: true },
		{ key: "last", header: "Last Name", cell: (row) => row.lastname ?? "—" },
		{ key: "id", header: "ID Number", cell: (row) => row.idNo ?? "—" },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[150px] tw-text-right",
			cell: (row) => (
				<div className="tw-flex tw-justify-end tw-gap-1.5">
					<CustomModal
						title={`Edit ${subAccountLabel}`}
						trigger={
							<button type="button" className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint">
								Edit
							</button>
						}
					>
						<AddSubAccount customer_id={customer?.id} subAccount={row} />
					</CustomModal>
					<CustomConfirmDialog
						trigger={
							<button type="button" className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-red hover:tw-bg-red-tint">
								Delete
							</button>
						}
						mutateFn={() => removeSubAccount({ uuid: row.id as string | number })}
					/>
				</div>
			),
		},
	];

	const name = [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") || customerLabel;

	return (
		<CustomModal
			open={Boolean(uuid)}
			onOpenChange={onOpenChange}
			size="normal"
			title={
				isLoading ? (
					<Skeleton className="tw-h-5 tw-w-40" />
				) : (
					<span className="tw-flex tw-items-center tw-gap-3">
						<span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-teal-tint tw-text-[14px] tw-font-bold tw-text-teal-dark">
							{initialsOf(customer?.first_name, customer?.last_name)}
						</span>
						<span className="tw-min-w-0">
							<span className="tw-block tw-truncate">{name}</span>
							<span className="tw-block tw-text-[12.5px] tw-font-normal tw-text-text-soft">
								{customer?.business_name || "—"}
								{customer?.customer_id ? ` · ID ${customer.customer_id}` : ""}
							</span>
						</span>
					</span>
				)
			}
			footer={
				customer && (
					<div className="tw-flex tw-flex-wrap tw-gap-2">
						<CustomConfirmDialog
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<FileText className="tw-h-4 tw-w-4" />
									Generate Account Statement
								</Button>
							}
							mutateFn={() => generateStatement({ uuid: customer.uuid, bvn: customer.bvn })}
						/>
						<CustomModal
							title={`Edit ${customerLabel}`}
							trigger={
								<Button size="sm" variant="outline" className="tw-gap-1.5">
									<Pencil className="tw-h-4 tw-w-4" />
									Edit
								</Button>
							}
						>
							<EditCustomerModal customer={customer} />
						</CustomModal>
						<CustomConfirmDialog
							trigger={
								<Button size="sm" variant="outline" disabled={isArchiving} className="tw-gap-1.5">
									{customer.is_archived ? <ArchiveRestore className="tw-h-4 tw-w-4" /> : <Archive className="tw-h-4 tw-w-4" />}
									{customer.is_archived ? "Unarchive" : "Archive"}
								</Button>
							}
							mutateFn={() => archiveMutate({ uuid: customer.uuid })}
						/>
					</div>
				)
			}
		>
			{isLoading ? (
				<div className="tw-space-y-4">
					<div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
						{[0, 1, 2].map((index) => (
							<Skeleton key={index} className="tw-h-[76px]" />
						))}
					</div>
					<Skeleton className="tw-h-32" />
				</div>
			) : !customer ? (
				<EmptyState title={`${customerLabel} not found`} description="This record may have been removed." />
			) : (
				<div className="tw-space-y-6">
					<div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
						<MiniStat label="Total Owed" value={renderCurrency(detail?.total_owed ?? 0)} />
						<MiniStat label="Total Paid" value={renderCurrency(detail?.total_payments_received ?? 0)} />
						<MiniStat label={`${invoiceLabel}s Issued`} value={detail?.total_invoice_count ?? 0} />
					</div>

					<section>
						<h4 className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">Contact Details</h4>
						<div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
							<Field label="Email Address" value={customer.email} />
							<Field label="Phone Number" value={customer.phone} />
							<div className="sm:tw-col-span-2">
								<Field label="Address" value={customer.address} />
							</div>
							<div className="sm:tw-col-span-2">
								<Field label="Business Name" value={customer.business_name} />
							</div>
						</div>
					</section>

					<section>
						<h4 className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">
							Transaction Details
						</h4>
						<div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
							<Field label={`Total ${invoiceLabel} Value`} value={renderCurrency(detail?.total_invoice_value ?? 0)} />
							<Field label="Total Payment Made" value={renderCurrency(detail?.total_payments_received ?? 0)} />
						</div>
					</section>

					{isSchool && (
						<section>
							<div className="tw-mb-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3">
								<h4 className="tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">
									{subAccountLabelPlural}
								</h4>
								<CustomModal
									title={`Add ${subAccountLabel}`}
									trigger={
										<Button size="sm" variant="outline" className="tw-gap-1.5">
											<Plus className="tw-h-4 tw-w-4" />
											Add {subAccountLabel}
										</Button>
									}
								>
									<AddSubAccount customer_id={customer.id} />
								</CustomModal>
							</div>
							<DataTable
								data={customer.sub_accounts ?? []}
								columns={subAccountColumns}
								rowKey={(row, index) => row.id ?? index}
								rowsPerPageOptions={[5, 10, "All"]}
								defaultRowsPerPage={5}
								emptyTitle={`No ${subAccountLabelPlural.toLowerCase()}`}
								emptyDescription={`This ${customerLabel.toLowerCase()} has no linked ${subAccountLabelPlural.toLowerCase()}.`}
							/>
						</section>
					)}
				</div>
			)}
		</CustomModal>
	);
};

export default CustomerDetailModal;
